/**
 * Desconectar um canal (30/09/2026), com a confirmação que o estrago pede.
 *
 * O motor apaga a caixa e as conversas dela, e as mensagens novas param de
 * chegar — sem desfazer. Então a janela diz isso com todas as letras e pede o
 * nome do canal digitado, como o motor faz ao excluir uma caixa. Depois vem a
 * reautenticação com justificativa (step-up), que o servidor exige.
 *
 * "Trocar a conta" de Facebook ou Instagram é isto, seguido de conectar de
 * novo pela grade.
 */

import { useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Botao, Campo, Dialogo } from '@/shared/ui';
import { Faixa } from '@/shared/ui/estados/faixa';
import { useApi } from '@/shared/api/consultas';
import { chaves } from '@/shared/api/chaves';
import { recadoDeErro } from '@/shared/api/recado-de-erro';
import type { UsoDeStepUp } from '@/features/acesso/confirmar-identidade';

export function DesconectarCanal({
  inboxId,
  nome,
  stepUp,
  aoFechar,
  aoDesconectar,
}: {
  inboxId: string;
  nome: string;
  stepUp: UsoDeStepUp;
  aoFechar: () => void;
  aoDesconectar: (nome: string) => void;
}): ReactNode {
  const api = useApi();
  const cache = useQueryClient();
  const [digitado, definirDigitado] = useState('');
  const [desconectando, definirDesconectando] = useState(false);
  const [erro, definirErro] = useState<string | null>(null);

  const confere = digitado.trim() === nome.trim();

  const desconectar = async () => {
    definirErro(null);
    definirDesconectando(true);
    try {
      await stepUp.comStepUp(async () => {
        await api!.desconectarCanal({ caminho: { id: inboxId } });
        void cache.invalidateQueries({ queryKey: chaves.canaisConectados() });
        void cache.invalidateQueries({ queryKey: chaves.canaisDeWhatsApp() });
        aoDesconectar(nome);
      }, inboxId);
    } catch (falha) {
      definirErro(recadoDeErro(falha, { generica: 'Não foi possível desconectar o canal.' }));
    } finally {
      definirDesconectando(false);
    }
  };

  return (
    <Dialogo
      titulo={`Desconectar ${nome}`}
      aoFechar={aoFechar}
      consequencia
      botoes={
        <>
          <Botao onClick={aoFechar}>Cancelar</Botao>
          <Botao
            variante="perigo"
            desabilitado={!confere || desconectando || api === null}
            motivo={
              api === null
                ? 'Sem conexão agora. Tente de novo em instantes.'
                : !confere
                  ? 'Digite o nome do canal para confirmar.'
                  : 'Desconectando…'
            }
            onClick={() => void desconectar()}
          >
            {desconectando ? 'Desconectando…' : 'Desconectar'}
          </Botao>
        </>
      }
    >
      <p>
        As conversas deste canal saem do Atendimento e as mensagens novas param de chegar. Não dá
        para desfazer: conectar de novo cria um canal sem o histórico.
      </p>
      <Campo
        rotulo={`Para confirmar, digite ${nome}`}
        value={digitado}
        autoComplete="off"
        onChange={(e) => definirDigitado(e.target.value)}
      />
      {erro && <Faixa tom="bloqueado">{erro}</Faixa>}
    </Dialogo>
  );
}
